import { h, Component } from 'preact'
import HeaderNavigationWithModules from '../header-navigation/index.sync'
import { z } from 'zod'
import { GetServerSideProps } from '../../server-middleware/context'
import getConfig from '../../services/configCommunity'
import { getGroups } from '../../server-middleware/groupRepository'
import { getCategories } from '../../server-middleware/categoryRepository'
import { prepCategories, decodeSsoUrl } from '../widget-navigation-bar/index.server'
import { CurrentUserContext, UserPermissionContext } from '../../server-middleware/userContext'
import Phrase from '../../libs/translationsUtils/phrase'
import UserFlag from '../User/Flag'
import Dropdown from '../User/Dropdown'
import { CategoryInfo } from '../header-navigation/Types'
import Search from '../algolia/components/Search'
import { ModulesContext } from '../../utils/globals'

function getCategoryId () {
  if (typeof window === 'undefined') return null
  return window.inSidedData.content.category.id
}

function isTopicNew () {
  if (typeof window === 'undefined') return null
  return window.location.pathname.match(/^\/topic\/new/)
}

function getContentType () {
  if (typeof window === 'undefined') return undefined
  if (window.location.pathname.match(/^\/ideas/)) {
    return ['ideation']
  }
  if (window.location.pathname.match(/^\/product-updates/)) {
    return ['productUpdate']
  }
  return undefined
}

export interface Props {
  isFederatedSalesforceSearch: boolean
  isFederatedFreshdeskSearch: boolean
  isFederatedSkilljarSearch: boolean
  items: Config['items']
  logo: Config['logo']
  groupCount: number
  communityCategories: CategoryInfo[]
  knowledgeBaseCategories: CategoryInfo[]
  communityCustomerTitle: string
  ssoLoginUrl: string
}

export const widgetName = 'header_navigation' as const

export const config = z.object({
  items: z.array(z.object({
    key: z.string(),
    visibility: z.boolean(),
    name: z.string()
  })),
  logo: z.string()
})

type Config = z.infer<typeof config>

export const getServerSideProps: GetServerSideProps<Config, Props> = async (context, config) => {
  return await context.cache(`header-navigation_${JSON.stringify(config)}_${context.userId}`, async () => {
    const list = prepCategories(await getCategories(context))
    const [isFederatedSalesforceSearch] = (await Promise.allSettled([
      getConfig(context, 'salesforceFederatedSearchEnabled')
    ])).map((result) => {
      if (result.status === 'fulfilled') return result.value || false
      return false
    })

    return {
      items: config.items,
      logo: config.logo,
      groupCount: (await getGroups(context)).length,
      communityCategories: list.filter(c => c.type !== 1),
      knowledgeBaseCategories: list.filter(c => c.type === 1),
      isFederatedSalesforceSearch: isFederatedSalesforceSearch,
      isFederatedFreshdeskSearch: context.config.config.params.freshdesk?.enabledFederatedSearch || false,
      isFederatedSkilljarSearch: context.config.config.params.skilljar?.enabledFederatedSearch || false,
      ssoLoginUrl: decodeSsoUrl(context.config.config.params),
      communityCustomerTitle: context.config.title
    }
  })
}

export default class HeaderNavigation extends Component<Props> {
  render (props: Props) {
    return (
      <CurrentUserContext.Consumer>
        {(user) => (
          <ModulesContext.Consumer>
            {modules => (
              <section class='main-navigation--wrapper header-navigation'>
                <div class='main-navigation-sitewidth'>
                  {props.logo
                    ? <div class='header-navigation_logo-wrapper'>
                      <a href='/' target='_self' class='header-navigation_logo-anchor'>
                        <img
                          class='header-navigation_logo' src={props.logo} title=''
                          alt={`${props.communityCustomerTitle} Logo`}
                        />
                      </a>
                    </div>
                    : null}
                  <UserPermissionContext.Consumer>
                    {(permissions) =>
                      <HeaderNavigationWithModules
                        items={props.items}
                        modules={modules}
                        groupCount={props.groupCount}
                        communityCategories={props.communityCategories}
                        knowledgeBaseCategories={props.knowledgeBaseCategories}
                        logo={props.logo}
                        showActivitySinceLastVisit={!!user.id}
                        displaySearchInWidget // TODO SUC-365 will make this to be true by default
                        searchInfo={{
                          isUserGuest: !user.id,
                          isFederatedSalesforceSearch: props.isFederatedSalesforceSearch,
                          isFederatedSkilljarSearch: props.isFederatedSkilljarSearch,
                          isFederatedFreshdeskSearch: props.isFederatedFreshdeskSearch,
                          category: getCategoryId() || undefined,
                          isParentCategory: false, // TODO implement this and isParentCategory when extending this feature to category page
                          searchByContentType: getContentType()
                        }}
                        permissions={{
                          ideation: permissions.permissions.includes('forum.idea.view'),
                          productUpdates: permissions.permissions.includes('forum.product.updates.view')
                        }}
                      />}
                  </UserPermissionContext.Consumer>

                  <section class='main-navigation--nav-buttons-wrapper js-main-navigation'>
                    <ul>
                      {!isTopicNew()
                        ? <li class='is-hidden-S'>
                          <Phrase p='main.navigation.add_topic'>
                            {(title) =>
                              <a
                                href={getCategoryId() ? `/topic/new?fid=${getCategoryId()}` : '/topic/new'}
                                data-track={JSON.stringify({ trigger: 'navigation', type: 'Topic Initiated' })}
                                data-ga-track={JSON.stringify({ eventCategory: 'Homepage', eventAction: 'Create topic clicked', eventLabel: { Position: 'Navigation' } })}
                                class='menu-create-topic qa-menu-create-topic btn btn--cta'
                                role='button'
                                title={title}
                              >
                                <div
                                  aria-hidden='true' class='header-navigation-button-icon' data-preact='assets/index'
                                  data-props={JSON.stringify({ icon: 'add', width: 16, height: 16 })}
                                />
                                <span>{title}</span>
                              </a>}
                          </Phrase>
                        </li>
                        : null}
                      <Phrase m='Common' p='main.navigation.login'>
                        {(loginPhrase) => !user.id
                          ? <li>
                            {props.ssoLoginUrl
                              ? <a
                                  role='button' href={props.ssoLoginUrl}
                                  class='header-login-button qa-header-login-button btn btn--secondary'
                                >
                                <div
                                  aria-hidden='true' class='header-navigation-button-icon'
                                  data-preact='assets/index' data-props={JSON.stringify({ icon: 'person' })}
                                />
                                <span>{loginPhrase}</span>
                              </a>
                              : <a
                                  role='button' href='#'
                                  class='js-open-modal header-login-button qa-header-login-button btn btn--secondary'
                                  data-modal='login'
                                >
                                <div
                                  class='header-navigation-button-icon' aria-hidden='true'
                                  data-preact='assets/index' data-props={JSON.stringify({ icon: 'person' })}
                                />
                                <span>{loginPhrase}</span>
                              </a>}
                          </li>
                          : <li class='main-navigation--profile-link qa-header-profile-link'>
                            <UserFlag {...user} />
                            <Dropdown {...user} />
                          </li>}
                      </Phrase>
                    </ul>
                  </section>
                </div>
                <div class='header-navigation-search_mobile'>
                  <div class='header-navigation_extendable-search extended is-visible-S'>
                    <div class='header-navigation_extended-search'>
                      <form method='get' action='/search/index'>
                        <div>
                          <Search {...{
                            isUserGuest: !user.id,
                            displayFilters: false,
                            isFederatedSalesforceSearch: props.isFederatedSalesforceSearch,
                            isFederatedSkilljarSearch: props.isFederatedSkilljarSearch,
                            isFederatedFreshdeskSearch: props.isFederatedFreshdeskSearch,
                            category: getCategoryId() || undefined,
                            isParentCategory: false, // TODO implement this and isParentCategory when extending this feature to category page
                            searchByContentType: getContentType()
                          }}
                          />
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </ModulesContext.Consumer>
        )}
      </CurrentUserContext.Consumer>
    )
  }
}
